import React, { useEffect, useState } from 'react';
import { Calendar, MapPin, Users, Award } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

export default function Events() {
    const [events, setEvents] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const { type } = useAuth();

    useEffect(() => {
        api.get('/events').then(({ data }) => setEvents(data));
        if (type === 'customer') {
            api.get('/events/registrations/my').then(({ data }) => setRegistrations(data)).catch(() => {});
        }
    }, [type]);

    const register = async (id) => {
        if (type !== 'customer') { toast.error('Please sign in to register'); return; }
        try {
            const { data } = await api.post(`/events/${id}/register`);
            toast.success(`Registered! QR: ${data.qr_code}`);
            setRegistrations((r) => [...r, data]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not register');
        }
    };

    const registeredIds = new Set(registrations.map((r) => r.event_id));

    return (
        <div data-testid="events-page" className="pt-16">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-16">
                <div className="overline mb-6">What's On</div>
                <h1 className="font-serif text-6xl md:text-7xl text-navy leading-[1.02] tracking-tight max-w-4xl">
                    Moments live <span className="italic text-gold">at Inorbit.</span>
                </h1>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32 grid md:grid-cols-2 gap-8" data-testid="events-grid">
                {events.map((e) => (
                    <div key={e.id} className="card-luxury overflow-hidden img-zoom" data-testid={`event-card-${e.id}`}>
                        <div className="aspect-[16/9] overflow-hidden relative">
                            <img src={e.image_url} alt="" className="w-full h-full object-cover" />
                            {e.is_ticketed && <div className="absolute top-4 right-4 bg-navy text-white text-[10px] tracking-widest uppercase px-3 py-1">Ticketed</div>}
                            {!e.tier_eligibility.includes('silver') && <div className="absolute top-4 left-4 bg-gold text-white text-[10px] tracking-widest uppercase px-3 py-1">{e.tier_eligibility[e.tier_eligibility.length - 1]} only</div>}
                        </div>
                        <div className="p-8">
                            <div className="overline text-gold mb-3">{e.event_type.replace('_', ' ')}</div>
                            <h3 className="font-serif text-3xl text-navy leading-tight mb-3">{e.title}</h3>
                            <p className="text-sm text-text-secondary leading-relaxed mb-6">{e.description}</p>
                            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-text-tertiary mb-6">
                                <span className="flex items-center gap-2"><MapPin size={12} strokeWidth={1.5} /> {e.mall_name} • {e.venue}</span>
                                <span className="flex items-center gap-2"><Calendar size={12} strokeWidth={1.5} /> {new Date(e.start_datetime).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                <span className="flex items-center gap-2"><Users size={12} strokeWidth={1.5} /> {e.registered_count}/{e.capacity}</span>
                                <span className="flex items-center gap-2"><Award size={12} strokeWidth={1.5} /> +{e.points_for_attending} pts</span>
                            </div>
                            <button
                                onClick={() => register(e.id)}
                                disabled={registeredIds.has(e.id)}
                                className={registeredIds.has(e.id) ? 'btn-outline w-full justify-center opacity-50' : 'btn-primary w-full justify-center'}
                                data-testid={`event-register-${e.id}`}
                            >
                                {registeredIds.has(e.id) ? 'Registered ✓' : (e.is_ticketed ? `Book — ₹${e.ticket_price}` : 'Register Free')}
                            </button>
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
}
